import { indexingMessage, mutationError, validateFile } from './acervoHelpers.js'

export const MAX_BATCH_FILES = 50

const fileKey = (file) => JSON.stringify([file.name, file.size, file.lastModified])
export const canImportRow = (row) => !row.documento && ['pending', 'failed'].includes(row.status)
export const canIndexRow = (row) => Boolean(row.documento && row.documento.status !== 'ARQUIVADO' &&
  row.hasText && !['ready', 'indexing'].includes(row.status))
export const needsIndex = (document, { incluirProntos = false, now = Date.now() } = {}) =>
  ['PENDENTE', 'FALHOU'].includes(document.status) || (incluirProntos && document.status === 'PRONTO') ||
  (document.status === 'INDEXANDO' && Boolean(document.indexacaoExpiraEm) &&
    new Date(document.indexacaoExpiraEm).valueOf() <= now)

export function appendBatchFiles(rows, files, defaults, createId) {
  const next = [...rows]
  const known = new Set(rows.map((row) => fileKey(row.file)))
  let duplicates = 0
  let excess = 0
  for (const file of files) {
    if (known.has(fileKey(file))) { duplicates += 1; continue }
    if (next.length >= MAX_BATCH_FILES) { excess += 1; continue }
    known.add(fileKey(file))
    const error = validateFile(file)
    next.push({ id: createId(), file, fields: { titulo: file.name.replace(/\.[^.]+$/, '').slice(0, 240),
      area: defaults.area || '', acao: defaults.acao || '', observacoes: '' },
    status: error ? 'invalid' : 'pending', error, warnings: [], documento: null, hasText: false })
  }
  return { rows: next, duplicates, excess }
}

export function validateBatchRow(row, catalog) {
  const fileError = validateFile(row.file)
  if (fileError) return fileError
  if (!row.fields.titulo.trim()) return 'Informe o título desta peça.'
  if (!catalog.areas.some((area) => area.key === row.fields.area)) return 'Selecione uma área jurídica.'
  if (!catalog.acoesCanonicas.some((action) => action.codigo === row.fields.acao))
    return 'Selecione uma ação ativa do motor.'
  return ''
}

const uncertainResponse = (result) => result.success || !result.status || result.status >= 500 || result.status === 408
const uncertainMessage = 'O envio ficou sem confirmação. Confira se esta peça foi salva na lista da base jurídica antes de enviá-la novamente. Este item não será reenviado automaticamente.'
const rateLimitMessage = 'O servidor está ocupado com outra operação. Aguarde alguns instantes e tente novamente os itens pendentes; os arquivos já salvos serão mantidos.'

// Keep uploads sequential: each original has an independent request and result.
// A confirmed save is never submitted again, including when indexing fails.
export async function importBatch(rows, { api, extractPdf, catalog, signal, shouldStop, onChange, onSaved }) {
  for (const row of rows) {
    if (signal?.aborted || shouldStop()) break
    if (!canImportRow(row)) continue
    const validation = validateBatchRow(row, catalog)
    if (validation) { onChange(row.id, { status: 'failed', error: validation }); continue }
    let conteudoTexto = ''
    let warnings = []
    if (/\.pdf$/i.test(row.file.name)) {
      onChange(row.id, { status: 'extracting', error: '', warnings: [], progress: null })
      try {
        const extraction = await extractPdf(row.file, { signal,
          onProgress: (progress) => onChange(row.id, { progress }) })
        conteudoTexto = extraction.conteudoTexto || ''
        warnings = extraction.avisos || []
      } catch (error) {
        if (signal?.aborted) break
        warnings = [`${error.message || 'Não foi possível extrair o texto.'} O original será salvo; revise o texto na edição da peça.`]
      }
    }
    if (signal?.aborted) break
    onChange(row.id, { status: 'saving', error: '', warnings, progress: null })
    try {
      const result = await api.create(row.file, { ...row.fields, conteudoTexto }, signal)
      if (result.success && result.data?.documento?.id) {
        const hasText = Boolean(result.data.conteudoTexto?.trim())
        onChange(row.id, { status: 'saved', documento: result.data.documento, hasText,
          error: '', warnings: hasText ? warnings : [...warnings, 'Original salvo sem texto para indexar. Abra a peça para adicionar ou revisar o texto.'] })
        onSaved?.()
      } else {
        onChange(row.id, { status: uncertainResponse(result) ? 'uncertain' : 'failed',
          error: result.status === 429 ? rateLimitMessage : uncertainResponse(result) ? uncertainMessage : mutationError(result) })
        if (result.status === 429) break
      }
    } catch {
      onChange(row.id, { status: 'uncertain', error: uncertainMessage })
    }
  }
}

function savedState(detail) {
  const documento = detail.documento
  return { documento, hasText: Boolean(detail.conteudoTexto?.trim()),
    status: documento.status === 'PRONTO' ? 'ready' : documento.status === 'INDEXANDO' ? 'indexing' : 'saved' }
}

export async function indexBatch(rows, { api, signal, shouldStop, onChange, onSaved, incluirProntos = false }) {
  for (const row of rows) {
    if (signal?.aborted || shouldStop()) break
    if (!canIndexRow(row)) continue
    onChange(row.id, { status: 'indexing', error: '' })
    try {
      // Reindexing also uses the current version, never the version from the initial list.
      const current = await api.get(row.documento.id, signal)
      if (signal?.aborted) break
      if (!current.success) {
        onChange(row.id, { status: 'indexFailed', error: mutationError(current) })
        continue
      }
      const state = savedState(current.data)
      const activeLease = state.documento.status === 'INDEXANDO' &&
        (!state.documento.indexacaoExpiraEm || new Date(state.documento.indexacaoExpiraEm).valueOf() > Date.now())
      const missingClassification = state.documento.status === 'SEM_AREA'
      if ((state.status === 'ready' && !incluirProntos) || activeLease || !state.hasText || missingClassification || state.documento.status === 'ARQUIVADO') {
        onChange(row.id, { ...state, status: activeLease ? 'indexPending' : state.status,
          error: activeLease ? 'A indexação continua no servidor. Atualize o estado antes de tentar novamente.'
            : state.documento.status === 'ARQUIVADO' ? 'Este documento foi arquivado. Abra a peça para conferir.'
              : missingClassification ? 'Informe a área e a ação na peça antes de indexar.' : '' })
        continue
      }
      const result = await api.index(state.documento.id, state.documento.version, signal)
      if (result.success) {
        const next = savedState(result.data)
        onChange(row.id, { ...next, status: next.documento.status === 'FALHOU' ? 'indexFailed'
          : next.status === 'indexing' ? 'indexPending' : next.status,
        error: next.documento.status === 'FALHOU' ? indexingMessage(next.documento.erroCodigo)
          : next.status === 'indexing' ? 'A indexação continua no servidor. Atualize o estado em alguns instantes.' : '' })
      } else onChange(row.id, { ...state, status: 'indexFailed', error: result.status === 429 ? rateLimitMessage
        : `${mutationError(result)} O original está salvo. A próxima tentativa conferirá o estado atual.` })
      onSaved?.()
      if (result.status === 429) break
    } catch {
      onChange(row.id, { status: 'indexFailed', error: 'Não foi possível confirmar a indexação. O original está salvo; a próxima tentativa conferirá o estado atual.' })
    }
  }
}
